import useReveal from "@/hooks/useReveal";
import { Zap, Wind, Mountain, Feather } from "lucide-react";

const features = [
  {
    icon: Zap,
    title: "Responsive Cushioning",
    copy: "Nitrogen-infused foam returns 82% of every step's energy.",
  },
  {
    icon: Wind,
    title: "Breathable Knit Upper",
    copy: "Engineered zonal ventilation keeps feet dry mile after mile.",
  },
  {
    icon: Mountain,
    title: "All-Terrain Grip",
    copy: "Multi-lug rubber outsole holds trail, track, and concrete.",
  },
  {
    icon: Feather,
    title: "Ultralight Build",
    copy: "Just 218g per shoe. You'll forget they're on.",
  },
];

export default function Features() {
  const ref = useReveal();

  return (
    <section
      ref={ref}
      data-testid="features-section"
      className="reveal bg-[#0e0e10] text-[#f5f3ee] py-24 md:py-32 relative overflow-hidden"
      id="features"
    >
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,87,34,0.6),transparent_60%)]" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="mono text-xs uppercase tracking-widest text-[#ff5722]">The tech</span>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] mt-3 tracking-tight max-w-2xl">
              Every gram, every stitch, engineered.
            </h2>
          </div>
          <p className="text-sm md:text-base text-white/60 max-w-sm">
            Four core innovations that separate STRYDE from the rack. Independently tested. Athlete approved.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="group relative p-7 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:-translate-y-2 transition-all duration-500 backdrop-blur-sm"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-[#ff5722] text-white flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform">
                  <Icon size={22} strokeWidth={2} />
                </div>
                <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{f.copy}</p>
                <span className="absolute top-6 right-6 mono text-xs text-white/30">0{i + 1}</span>
              </div>
            );
          })}
        </div>

        {/* Spotlight */}
        <div className="mt-16 md:mt-20 grid lg:grid-cols-12 gap-10 items-center rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8 md:p-14">
          <div className="lg:col-span-6 relative">
            <img
              src="https://images.unsplash.com/photo-1584735175315-9d5df23860e6?auto=format&fit=crop&w=1200&q=85"
              alt="Cutaway view of Meridian X sneaker showing tech layers"
              className="w-full h-auto rounded-2xl"
              loading="lazy"
            />
            {/* Callout labels */}
            <div className="absolute top-6 left-6 md:top-10 md:left-10">
              <span className="inline-block mono text-[10px] px-2 py-1 rounded bg-[#ff5722] text-white tracking-widest">01 · CARBON PLATE</span>
            </div>
            <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10">
              <span className="inline-block mono text-[10px] px-2 py-1 rounded bg-white text-black tracking-widest">02 · N2 FOAM</span>
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="mono text-xs uppercase tracking-widest text-[#ff5722]">Spotlight — Meridian X</span>
            <h3 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl leading-tight mt-3">
              A propulsion system disguised as a shoe.
            </h3>
            <p className="mt-5 text-white/70 leading-relaxed">
              A full-length carbon-fibre plate paired with our new N2 supercritical foam creates a snap-forward geometry that shaves seconds off every mile — without punishing your joints on the way back down.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/80">
              <li className="flex gap-3"><span className="mono text-[#ff5722]">▸</span> 4mm carbon plate, forged in one piece</li>
              <li className="flex gap-3"><span className="mono text-[#ff5722]">▸</span> 38% higher energy return vs. our last generation</li>
              <li className="flex gap-3"><span className="mono text-[#ff5722]">▸</span> Rock plate + trail-ready outsole compound</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
